import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { fmtDate } from '../lib/fmt';

type Account = { id: string; name: string; account_type: string; currency: string; opening_balance: number };
type Line = { id: string; line_date: string; description: string | null; amount: number; status: string; matched_payment_id: string | null; matched_supplier_payment_id: string | null };

function parseDate(s: string): string | null {
  const v = s.trim();
  let m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = v.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})/); // DD/MM/YYYY
  if (m) return `${m[3]}-${(m[2] ?? '').padStart(2, '0')}-${(m[1] ?? '').padStart(2, '0')}`;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}
function parseCsv(text: string): string[][] {
  return text.replace(/\r/g, '').split('\n').filter((l) => l.trim()).map((l) => l.split(',').map((c) => c.trim().replace(/^"|"$/g, '')));
}

export function Treasury() {
  const { t, i18n } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const loc = i18n.language;
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [newName, setNewName] = useState('');
  const [rows, setRows] = useState<string[][]>([]);
  const [hasHeader, setHasHeader] = useState(true);
  const [map, setMap] = useState<{ date: number; desc: number; amount: number }>({ date: 0, desc: 1, amount: 2 });
  const [busy, setBusy] = useState(false);
  const money = (n: number, c?: string) => formatCurrency(n, c || currency, loc);

  const loadAccounts = useCallback(async () => {
    const { data } = await supabase.from('bank_accounts').select('id,name,account_type,currency,opening_balance').is('deleted_at', null).order('name');
    setAccounts((data as any) ?? []);
  }, []);
  useEffect(() => { void loadAccounts(); }, [loadAccounts]);

  const loadLines = useCallback(async (acc: string) => {
    const { data } = await supabase.from('bank_statement_lines').select('id,line_date,description,amount,status,matched_payment_id,matched_supplier_payment_id').eq('bank_account_id', acc).is('deleted_at', null).order('line_date', { ascending: false });
    setLines((data as any) ?? []);
  }, []);
  useEffect(() => { if (sel) void loadLines(sel); }, [sel, loadLines]);

  async function addAccount() {
    if (!newName.trim()) return;
    await supabase.from('bank_accounts').insert({ organization_id: orgId, name: newName.trim(), currency });
    setNewName(''); await loadAccounts();
  }
  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setRows(parseCsv(await f.text()));
  }
  async function doImport() {
    if (!sel) return;
    const body = (hasHeader ? rows.slice(1) : rows).map((r) => ({
      line_date: parseDate(r[map.date] ?? ''),
      description: r[map.desc] ?? '',
      amount: Number((r[map.amount] ?? '0').replace(/[^\d.-]/g, '')),
    })).filter((l) => l.line_date && !isNaN(l.amount));
    if (!body.length) return;
    setBusy(true);
    await supabase.rpc('import_bank_statement', { p_bank_account: sel, p_name: `Import ${new Date().toISOString().slice(0, 10)}`, p_lines: body });
    setBusy(false); setRows([]); await loadLines(sel);
  }
  const act = async (fn: string, id: string) => { await supabase.rpc(fn, { p_line: id }); if (sel) await loadLines(sel); };

  const groups = useMemo(() => ({
    suggested: lines.filter((l) => l.status === 'suggested'),
    unmatched: lines.filter((l) => l.status === 'unmatched'),
    reconciled: lines.filter((l) => l.status === 'reconciled'),
  }), [lines]);
  const headerRow = rows[0] ?? [];

  return (
    <Layout header={<Topbar />}>
      <div className="flex min-h-0 flex-1">
        <div className="w-[300px] shrink-0 overflow-y-auto border-r border-border p-3">
          <div className="mb-3 flex gap-2">
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('tr.accountName')} className="flex-1 rounded-md border border-border bg-card px-2.5 py-2 text-sm" />
            <Button onClick={addAccount}>+</Button>
          </div>
          {accounts.map((a) => (
            <button key={a.id} onClick={() => setSel(a.id)} className={cn('flex w-full items-center justify-between border-b border-divider px-2 py-2.5 text-left text-sm', sel === a.id ? 'bg-bg' : '')}>
              <div><div className="font-medium">{a.name}</div><div className="text-xs text-muted">{t('tr.type_' + a.account_type)} · {a.currency}</div></div>
            </button>
          ))}
          {accounts.length === 0 && <div className="px-2 py-6 text-center text-xs text-muted">{t('common.noData')}</div>}
        </div>

        <div className="min-w-0 flex-1 overflow-y-auto p-6">
          {!sel && <p className="text-sm text-muted">{t('tr.selectHint')}</p>}
          {sel && (
            <div className="space-y-6">
              <Card className="space-y-3 p-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold">{t('tr.importCsv')}</h2>
                  <input type="file" accept=".csv,text/csv" onChange={onFile} className="text-xs" />
                </div>
                {rows.length > 0 && (
                  <>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} /> {t('tr.hasHeader')}</label>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      {(['date', 'desc', 'amount'] as const).map((k) => (
                        <label key={k} className="flex flex-col gap-1">
                          <span className="text-xs text-muted">{t('tr.col_' + k)}</span>
                          <select value={(map as any)[k]} onChange={(e) => setMap((p) => ({ ...p, [k]: Number(e.target.value) }))} className="rounded-md border border-border bg-card px-2 py-1.5">
                            {(hasHeader ? headerRow : headerRow.map((_, i) => `#${i + 1}`)).map((h, i) => <option key={i} value={i}>{h || `#${i + 1}`}</option>)}
                          </select>
                        </label>
                      ))}
                    </div>
                    <div className="text-xs text-muted">{t('tr.rowsDetected', { n: hasHeader ? rows.length - 1 : rows.length })}</div>
                    <Button onClick={doImport} disabled={busy}>{t('tr.import')}</Button>
                  </>
                )}
              </Card>

              {[['suggested', groups.suggested], ['unmatched', groups.unmatched], ['reconciled', groups.reconciled]].map(([key, list]) => (
                <Card key={key as string} className="overflow-hidden p-0">
                  <div className="flex items-center justify-between border-b border-border px-4 py-2.5 text-sm font-medium">
                    <span>{t('tr.grp_' + key)}</span>
                    {key === 'unmatched' && (list as Line[]).length > 0 && <StatusPill tone="overdue">{t('tr.discrepancy')}</StatusPill>}
                  </div>
                  <div className="divide-y divide-divider">
                    {(list as Line[]).map((l) => (
                      <div key={l.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                        <div className="min-w-0"><span className="text-xs text-muted">{fmtDate(l.line_date, loc)}</span> <span className="truncate">{l.description}</span></div>
                        <div className="flex items-center gap-2">
                          <span className={cn('font-mono', l.amount < 0 ? 'text-st-overdue-fg' : '')}>{money(l.amount)}</span>
                          {l.status === 'suggested' && <>
                            <Button variant="ghost" onClick={() => act('confirm_reconciliation', l.id)}>{t('tr.confirm')}</Button>
                            <Button variant="ghost" onClick={() => act('reject_reconciliation', l.id)}>{t('tr.reject')}</Button>
                          </>}
                          {l.status === 'reconciled' && <StatusPill tone="done">{t('tr.reconciled')}</StatusPill>}
                        </div>
                      </div>
                    ))}
                    {(list as Line[]).length === 0 && <div className="px-4 py-3 text-xs text-muted">{t('common.noData')}</div>}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
